import React, { useState, useEffect, useRef, useMemo } from 'react'
import { createChart } from 'lightweight-charts'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { formattedNum } from '../../utils'
import styled from 'styled-components'
import { usePrevious } from 'react-use'
import { Play } from 'react-feather'
import { useDarkModeManager } from '../../contexts/LocalStorage'
import { IconWrapper } from '..'

dayjs.extend(utc)

export const CHART_TYPES = {
  BAR: 'BAR',
  AREA: 'AREA',
}

const Wrapper = styled.div`
  position: relative;
`

// constant height for charts
const HEIGHT = 300

const TradingViewChart = ({
  type = CHART_TYPES.BAR,
  data,
  base,
  baseChange,
  field,
  title,
  width,
  useWeekly = false,
  isUSD = true,
  titleOutOfChart = false,
  customizedTooltip = false,
}) => {
  // reference for DOM element to create with chart
  const ref = useRef()

  // pointer to the chart object
  const [chartCreated, setChartCreated] = useState(false)
  const dataPrev = usePrevious(data)
  const tooltipRef = useRef()
  const customizedTooltipRef = useRef()

  useEffect(() => {
    if (data !== dataPrev && chartCreated && type === CHART_TYPES.BAR) {
      // remove the tooltip element
      let tooltip = document.getElementById('tooltip-id' + type)
      let node = document.getElementById('test-id' + type)
      node.removeChild(tooltip)
      chartCreated.resize(0, 0)
      setChartCreated()
    }
  }, [chartCreated, data, dataPrev, type])

  const formattedData = useMemo(() => {
    chartCreated && chartCreated.remove()
    setChartCreated()
    // parse the data and format for tardingview consumption
    return data?.map((entry) => ({
      time: dayjs.unix(entry.date).utc().format('YYYY-MM-DD'),
      value: parseFloat(entry[field]),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, field])

  // adjust the scale based on the type of chart
  const topScale = type === CHART_TYPES.AREA ? (titleOutOfChart ? 0.08 : 0.32) : 0.2

  const [darkMode] = useDarkModeManager()
  const textColor = darkMode ? 'white' : 'black'
  const previousTheme = usePrevious(darkMode)

  const bindStyles = (item, styles) => {
    Object.keys(styles).forEach((key) => {
      item.style[key] = styles[key]
    })
  }

  // reset the chart if them switches
  useEffect(() => {
    if (chartCreated && previousTheme !== darkMode) {
      // remove the tooltip element
      let tooltip = document.getElementById('tooltip-id' + type)
      let node = document.getElementById('test-id' + type)
      node.removeChild(tooltip)
      chartCreated.resize(0, 0)
      setChartCreated()
    }
  }, [chartCreated, darkMode, previousTheme, type])

  // if no chart created yet, create one with options and add to DOM manually
  useEffect(() => {
    if (!chartCreated && formattedData) {
      var chart = createChart(ref.current, {
        width: width,
        height: HEIGHT,
        layout: {
          backgroundColor: 'transparent',
          textColor: textColor,
        },
        rightPriceScale: {
          scaleMargins: {
            top: topScale,
            bottom: 0,
          },
          borderVisible: false,
        },
        timeScale: {
          borderVisible: false,
        },
        grid: {
          horzLines: {
            color: 'rgba(197, 203, 206, 0.5)',
            visible: false,
          },
          vertLines: {
            color: 'rgba(197, 203, 206, 0.5)',
            visible: false,
          },
        },
        crosshair: {
          horzLine: {
            visible: false,
            labelVisible: false,
          },
          vertLine: {
            visible: true,
            style: 0,
            width: 2,
            color: 'rgba(32, 38, 46, 0.1)',
            labelVisible: false,
          },
        },
        localization: {
          priceFormatter: (val) => (isUSD ? formattedNum(val, true) : val),
        },
      })

      var series =
        type === CHART_TYPES.BAR
          ? chart.addHistogramSeries({
              color: '#25D366',
              priceFormat: {
                type: 'volume',
              },
              scaleMargins: {
                top: 0.32,
                bottom: 0,
              },
              lineColor: '#25D366',
              lineWidth: 3,
            })
          : chart.addAreaSeries({
              topColor: '#25D366',
              bottomColor: '#212121',
              lineColor: '#25D366',
              lineWidth: 3,
            })

      series.setData(formattedData)

      var toolTip
      if (tooltipRef.current) {
        toolTip = tooltipRef.current
      } else {
        toolTip = document.createElement('div')
        tooltipRef.current = toolTip
      }
      toolTip.setAttribute('id', 'tooltip-id' + type)
      toolTip.className = darkMode ? 'three-line-legend-dark' : 'three-line-legend'
      ref.current.appendChild(toolTip)
      toolTip.style.display = 'block'
      toolTip.style.fontWeight = '500'
      toolTip.style.backgroundColor = 'transparent'
      toolTip.style.left = '-4px'
      if (titleOutOfChart) {
        toolTip.style.top = '-3.5rem'
      } else {
        toolTip.style.top = '-8px'
      }

      var customTooltip

      if (customizedTooltip) {
        if (customizedTooltipRef.current) {
          customTooltip = customizedTooltipRef.current
        } else {
          customTooltip = document.createElement('div')
          customizedTooltipRef.current = customTooltip
        }

        customTooltip.style.display = 'none'
        customTooltip.setAttribute('id', 'tooltip-id' + type)
        customTooltip.className = darkMode ? 'three-line-legend-dark' : 'three-line-legend'
        ref.current.appendChild(customTooltip)
      }

      // format numbers
      let percentChange = baseChange?.toFixed(2)
      let formattedPercentChange = baseChange ? (percentChange > 0 ? '+' : '') + percentChange + '%' : ''
      let color = percentChange >= 0 ? 'green' : 'red'

      // get the title of the chart
      function setLastBarText() {
        let tooltipTitle = isUSD ? formattedNum(base, true) : base

        const styles = {
          title: {
            fontSize: '16px',
            margin: '4px 0px',
            color: textColor,
          },
          tooltipTitle: {
            fontSize: '22px',
            margin: '4px 0px',
            color: textColor,
          },
          change: {
            marginLeft: '10px',
            fontSize: '16px',
            color: color,
          },
        }

        const titleText = document.createElement('div')
        const tooltipTitleText = document.createElement('div')
        const changeText = document.createElement('span')

        titleText.innerHTML = `${title} ${type === CHART_TYPES.BAR && !useWeekly ? '(24hr)' : ''}`
        tooltipTitleText.innerHTML = tooltipTitle
        changeText.innerHTML = formattedPercentChange

        bindStyles(titleText, styles.title)
        bindStyles(tooltipTitleText, styles.tooltipTitle)
        bindStyles(changeText, styles.change)

        tooltipTitleText.appendChild(changeText)

        toolTip.innerHTML = ''
        toolTip.appendChild(titleText)
        toolTip.appendChild(tooltipTitleText)
      }
      setLastBarText()

      // update the title when hovering on the chart
      chart.subscribeCrosshairMove(function (param) {
        if (
          param === undefined ||
          param.time === undefined ||
          param.point.x < 0 ||
          param.point.x > width ||
          param.point.y < 0 ||
          param.point.y > HEIGHT
        ) {
          setLastBarText()
        } else {
          var price = param.seriesPrices.get(series)
          const dateString = [param.time.year, param.time.month, param.time.day].join('-')
          if (customizedTooltip) {
            const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
            const startOfWeek = dayjs(dateString).startOf('week')
            const endOfWeek = dayjs(dateString).endOf('week')
            let newDate = useWeekly
              ? `${startOfWeek.date()} ${MONTHS[startOfWeek.month()]} - 
              ${endOfWeek.date()} ${MONTHS[endOfWeek.month()]} `
              : `${MONTHS[param.time.month - 1]} ${param.time.day}`

            const styles = {
              container: {
                width: '125px',
                height: '60px',
                left: `${param.point.x}px`,
                top: `${param.point.y}px`,
                transform: 'translate(-50%, -110%)',
                backgroundColor: '#3A3B3C',
                borderRadius: '6px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-around',
                alignItems: 'center',
                margin: '0px',
                padding: '10px 0px',
              },
              date: {
                fontSize: `${useWeekly ? 14 : 16}px`,
              },
              price: {
                color: '#16D36A',
                fontSize: '18px',
                padding: '0px',
                margin: '0px',
              },
            }

            const dateText = document.createElement('div')
            const priceText = document.createElement('div')

            dateText.innerHTML = newDate
            priceText.innerHTML = isUSD ? formattedNum(price, true) : price
            bindStyles(dateText, styles.date)

            bindStyles(priceText, styles.price)
            bindStyles(customTooltip, styles.container)

            customTooltip.innerHTML = ''
            customTooltip.appendChild(dateText)
            customTooltip.appendChild(priceText)
          } else {
            let dateStr = useWeekly
              ? dayjs(dateString).startOf('week').format('MMMM D, YYYY') +
                '-' +
                dayjs(dateString).endOf('week').format('MMMM D, YYYY')
              : dayjs(dateString).format('MMMM D, YYYY')

            const styles = {
              title: {
                fontSize: '16px',
                margin: '4px 0px',
                color: textColor,
              },
              price: {
                fontSize: '22px',
                margin: '4px 0px',
                color: textColor,
              },
            }

            const titleText = document.createElement('div')
            const priceText = document.createElement('div')
            const dateText = document.createElement('div')

            toolTip.innerHTML = ''
            titleText.innerHTML = title
            priceText.innerHTML = isUSD ? formattedNum(price, true) : price
            dateText.innerHTML = dateStr

            bindStyles(titleText, styles.title)
            bindStyles(priceText, styles.price)

            const toolTipChilds = [titleText, priceText, dateText]
            toolTipChilds.forEach((item) => {
              toolTip.appendChild(item)
            })
          }
        }
      })

      chart.timeScale().fitContent()

      setChartCreated(chart)
    }
  }, [
    base,
    baseChange,
    chartCreated,
    darkMode,
    data,
    formattedData,
    textColor,
    title,
    topScale,
    type,
    useWeekly,
    width,
    isUSD,
    titleOutOfChart,
    customizedTooltip,
  ])

  // responsiveness
  useEffect(() => {
    if (width) {
      chartCreated && chartCreated.resize(width, HEIGHT)
      chartCreated && chartCreated.timeScale().scrollToPosition(0)
    }
  }, [chartCreated, width])

  const removeCustomizedToolTip = () => {
    if (customizedTooltipRef.current) {
      customizedTooltipRef.current.style.display = 'none'
    }
  }

  return (
    <Wrapper>
      <div ref={ref} id={'test-id' + type} onMouseLeave={removeCustomizedToolTip} />
      <IconWrapper>
        <Play
          onClick={() => {
            chartCreated && chartCreated.timeScale().fitContent()
          }}
        />
      </IconWrapper>
    </Wrapper>
  )
}

export default TradingViewChart
